// USA-1255: each search runs as a word search or an AI search (smart filters + AI overview).
// A plain-word rule picks the mode as the shopper types; the search-bar switch overrides it.
export type SearchMode = 'keywords' | 'ai'

// A model number mixes letters and digits ("PRO-26R-N-CRT", "KM-515MAH", "T49") or joins
// digit groups with dashes ("1048-2").
const MODEL_TOKEN = /^(?=[a-z0-9./-]*\d)(?=[a-z0-9./-]*[a-z-])[a-z0-9]+(?:[./-][a-z0-9]+)*$/
const SPEC_UNITS =
  'in|inch|inches|"|ft|feet|foot|lb|lbs|pound|pounds|qt|quart|quarts|gal|gallon|gallons|oz|btu|btus|v|volt|volts|amp|amps|hp|w|watt|watts|kw|cu|door|doors|burner|burners|pan|pans|shelf|shelves|deck|decks|compartment|compartments|well|wells|tier|tiers|basket|baskets|drawer|drawers|day|hr|hour|person|people|seat|seats'
// A measurement or count: "25 inches", '48"', "500lb", "two doors", "single deck".
const SPEC = new RegExp(
  `(^|\\s)(\\d+(\\.\\d+)?|one|two|three|four|five|six|eight|ten|single|double|triple)[\\s-]?(${SPEC_UNITS})(?=\\s|$|[.,])`,
)
const QUESTION_START =
  /^(what|which|how|who|where|why|when|(can|could|do|does|should) (i|you|we)|is there|are there|i need|i want|need|looking for|recommend|show me|find me|help)\b/
// Words that relate, compare or rank rather than name a product.
const DESCRIBING_WORDS = new Set([
  'with',
  'without',
  'for',
  'under',
  'over',
  'below',
  'above',
  'than',
  'between',
  'and',
  'or',
  'vs',
  'versus',
  'that',
  'which',
  'least',
  'most',
  'large',
  'larger',
  'largest',
  'big',
  'bigger',
  'biggest',
  'small',
  'smaller',
  'smallest',
  'compact',
  'mini',
  'tiny',
  'wide',
  'narrow',
  'tall',
  'short',
  'cheap',
  'cheaper',
  'cheapest',
  'inexpensive',
  'affordable',
  'budget',
  'expensive',
  'premium',
  'best',
  'good',
  'better',
  'quiet',
  'fast',
  'faster',
  'fastest',
  'quick',
  'efficient',
  'durable',
  'reliable',
  'popular',
  'recommended',
  'stock',
  'ships',
  'shipping',
  // How soon it is needed (USA-1254): "fryer by friday", "ice machine asap".
  'ship',
  'asap',
  'rush',
  'today',
  'tomorrow',
  'week',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
])
const MAX_KEYWORD_WORDS = 3

function normalize(query: string) {
  return query.trim().toLowerCase().replace(/\s+/g, ' ')
}

function words(normalized: string) {
  return normalized ? normalized.split(' ') : []
}

function isModelToken(token: string) {
  return token.length >= 3 && MODEL_TOKEN.test(token) && !SPEC.test(token)
}

/** A model-number search, alone or with a brand ("true t-49"). */
export function isModelSearch(query: string) {
  const tokens = words(normalize(query))
  return tokens.length <= MAX_KEYWORD_WORDS && tokens.some(isModelToken)
}

/** Keywords for a model number or a few plain words; AI for anything that describes a need. */
export function classifySearchQuery(query: string): SearchMode {
  const normalized = normalize(query)
  if (!normalized) return 'keywords'
  if (normalized.includes('?') || QUESTION_START.test(normalized)) return 'ai'
  if (isModelSearch(normalized)) return 'keywords'
  const tokens = words(normalized.replace(/[^a-z0-9"\s-]/g, ' ').replace(/\s+/g, ' ').trim())
  if (tokens.length > MAX_KEYWORD_WORDS) return 'ai'
  if (SPEC.test(normalized) || tokens.some((token) => DESCRIBING_WORDS.has(token))) return 'ai'
  return 'keywords'
}
